// src/pages/MemberDetailPage.jsx
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Grid, Container, Breadcrumbs, Link, Paper, Avatar, Chip, Button,
  useTheme, alpha, Divider, Card, CardContent, IconButton, Tooltip, Skeleton
} from '@mui/material';
import {
  NavigateNext as NavigateNextIcon,
  Email as EmailIcon,
  Business as BusinessIcon,
  Public as PublicIcon,
  CalendarMonth as CalendarMonthIcon,
  Verified as VerifiedIcon,
  ArrowBack as ArrowBackIcon,
  AccessTime as AccessTimeIcon,
  WorkOutline as WorkOutlineIcon,
  Science as ScienceIcon,
  Share as ShareIcon,
  PersonAdd as PersonAddIcon,
  HourglassEmpty as PendingIcon,
  Message as MessageIcon
} from '@mui/icons-material';

// Import API service
import { getMemberById } from '../services/user';
import { getConnectionStatus, sendConnectionRequest, type ConnectionStatus } from '../services/connection';
import { useAuth } from '../context/AuthContext';

const MemberDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const { user: currentUser } = useAuth();
  interface Member {
    id: string;
    memberType: string;
    firstName: string;
    lastName: string;
    title: string;
    experience: string;
    photo: string;
    email: string;
    organization: {
      name: string;
      brief: string;
      segment: string;
      country: string;
    };
    dateJoined: string;
    lastActive: string;
    selectedSectors: string[];
    verified: boolean;
  }
  
  const [member, setMember] = useState<Member | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus | null>(null);
  const [loadingConnection, setLoadingConnection] = useState(false);

  useEffect(() => {
    const fetchMemberData = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getMemberById(id!);

        // Transform API data to match component expectations
        const transformedMember: Member = {
          id: data.id,
          memberType: data.userType,
          firstName: data.firstName,
          lastName: data.lastName,
          title: data.title || '',
          experience: data.experience || '',
          photo: data.photoUrl || '',
          email: data.email,
          organization: {
            name: data.organizationName || '',
            brief: data.organizationBrief || '',
            segment: data.segment || '',
            country: data.country || ''
          },
          dateJoined: data.createdAt || new Date().toISOString(),
          lastActive: data.updatedAt || new Date().toISOString(),
          selectedSectors: data.selectedSectors || [],
          verified: data.status === 'approved'
        };

        setMember(transformedMember);

        // Fetch connection status if user is logged in and viewing another user's profile
        if (currentUser && currentUser.id !== id) {
          try {
            const status = await getConnectionStatus(id!);
            setConnectionStatus(status);
          } catch (err) {
            console.error('Error fetching connection status:', err);
          }
        }
      } catch (err: any) {
        console.error('Error fetching member:', err);
        // Extract message from error object if it exists, otherwise use error as string
        const errorMessage = typeof err.response?.data?.error === 'string'
          ? err.response?.data?.error
          : err.response?.data?.error?.message || 'Member not found';
        setError(errorMessage);
        setMember(null);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchMemberData();
    }
  }, [id, currentUser]);

  // Format date function
  const formatDate = (dateString: string | number | Date) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Format time since last active
  const formatTimeSince = (dateString: string | number | Date) => {
    const now = new Date();
    const lastActive = new Date(dateString);
    const diffTime = Math.abs(now.getTime() - lastActive.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else if (diffDays < 30) {
      return `${Math.floor(diffDays / 7)} weeks ago`;
    } else {
      return `${Math.floor(diffDays / 30)} months ago`;
    }
  };

  // Handle contact button click
  const handleContactClick = () => {
    if (member?.email) {
      window.location.href = `mailto:${member?.email}`;
    }
  };

  // Handle share button click
  const handleShareClick = () => {
    if (navigator.share) {
      navigator.share({
        title: `${member?.firstName} ${member?.lastName} - Profile`,
        text: `Check out ${member?.firstName}'s profile`,
        url: window.location.href,
      });
    } else {
      // Fallback - copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      alert('Profile link copied to clipboard!');
    }
  };

  // Handle connect button click
  const handleConnectClick = async () => {
    if (!id || !currentUser) return;

    setLoadingConnection(true);
    try {
      await sendConnectionRequest(id);
      // Refresh connection status
      const status = await getConnectionStatus(id);
      setConnectionStatus(status);
      alert('Connection request sent!');
    } catch (err: any) {
      console.error('Error sending connection request:', err);
      const errorMessage = typeof err.response?.data?.error === 'string'
        ? err.response?.data?.error
        : err.response?.data?.error?.message || 'Failed to send connection request';
      alert(errorMessage);
    } finally {
      setLoadingConnection(false);
    }
  };

  // Handle message button click
  const handleMessageClick = () => {
    if (id) {
      navigate(`/messages/${id}`);
    }
  };

  // Loading state
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" color="primary" />}
          aria-label="breadcrumb"
          sx={{ mb: 4 }}
        >
          <Link
            color="inherit"
            href="/"
            onClick={(e) => { e.preventDefault(); navigate('/'); }}
            sx={{ textDecoration: 'none' }}
          >
            Home
          </Link>
          <Link
            color="inherit"
            href="/members"
            onClick={(e) => { e.preventDefault(); navigate('/members'); }}
            sx={{ textDecoration: 'none' }}
          >
            Members
          </Link>
          <Typography color="text.primary">Member Profile</Typography>
        </Breadcrumbs>

        <Paper
          elevation={2}
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            mb: 4
          }}
        >
          <Box
            sx={{
              height: 200,
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              position: 'relative'
            }}
          />
          <Box sx={{ p: 3, mt: -10 }}>
            <Skeleton variant="circular" width={150} height={150} sx={{ mb: 2 }} />
            <Skeleton variant="text" height={60} width="60%" sx={{ mb: 1 }} />
            <Skeleton variant="text" height={30} width="40%" sx={{ mb: 2 }} />
            <Skeleton variant="rectangular" height={60} sx={{ mb: 3 }} />
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Skeleton variant="rectangular" height={200} sx={{ mb: 3 }} />
              </Grid>
              <Grid item xs={12} md={4}>
                <Skeleton variant="rectangular" height={200} />
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Container>
    );
  }

  // Error state
  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper
          elevation={2}
          sx={{
            p: 4,
            borderRadius: 3,
            textAlign: 'center',
            backgroundColor: alpha(theme.palette.error.light, 0.1)
          }}
        >
          <Typography variant="h5" color="error" gutterBottom>
            {error}
          </Typography>
          <Typography variant="body1" gutterBottom>
            The member you're looking for couldn't be found or an error occurred.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate('/members')}
            startIcon={<ArrowBackIcon />}
            sx={{ mt: 2 }}
          >
            Back to Members
          </Button>
        </Paper>
      </Container>
    );
  }

  // Render member details
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Breadcrumbs */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" color="primary" />}
          aria-label="breadcrumb"
        >
          <Link
            color="inherit"
            href="/"
            onClick={(e) => { e.preventDefault(); navigate('/'); }}
            sx={{ textDecoration: 'none' }}
          >
            Home
          </Link>
          <Link
            color="inherit"
            href="/members"
            onClick={(e) => { e.preventDefault(); navigate('/members'); }}
            sx={{ textDecoration: 'none' }}
          >
            Members
          </Link>
          <Typography color="text.primary">
            {member ? `${member?.firstName} ${member?.lastName}` : ''}
          </Typography>
        </Breadcrumbs>

        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
        >
          Back
        </Button>
      </Box>

      {/* Member Profile Header */}
      <Paper
        elevation={3}
        sx={{
          borderRadius: 3,
          overflow: 'hidden',
          mb: 4
        }}
      >
        {/* Cover background */}
        <Box
          sx={{
            height: 200,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.7)}, ${alpha(theme.palette.secondary.main, 0.7)})`,
            position: 'relative'
          }}
        />

        {/* Profile content */}
        <Box sx={{ px: { xs: 3, md: 5 }, pb: { xs: 3, md: 5 }, mt: -10, position: 'relative' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Avatar
              src={member?.photo || `https://source.unsplash.com/random/150x150/?portrait&saudi&${member?.id}`}
              alt={`${member?.firstName || ''} ${member?.lastName || ''}`}
              sx={{
                width: 150,
                height: 150,
                border: `5px solid ${theme.palette.background.paper}`,
                boxShadow: '0 4px 10px rgba(0,0,0,0.1)'
              }}
            />
            
            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              <Tooltip title="Share profile">
                <IconButton
                  onClick={handleShareClick}
                  sx={{
                    backgroundColor: theme.palette.background.paper,
                    boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                    '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.1) }
                  }}
                >
                  <ShareIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
                {member ? `${member?.firstName} ${member?.lastName}` : ''}
              </Typography>
              {member?.verified && (
                <Tooltip title="Verified Member">
                  <VerifiedIcon color="primary" />
                </Tooltip>
              )}
            </Box>
            
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {member?.title}
            </Typography>

            <Typography 
              variant="body1" 
              sx={{ 
                mt: 2, 
                mb: 3, 
                maxWidth: 800,
                backgroundColor: alpha(theme.palette.background.paper, 0.7),
                p: 2,
                borderRadius: 2
              }}
            >
              {member?.experience}
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Chip
                icon={<BusinessIcon fontSize="small" />}
                label={member?.organization.name}
                color="primary"
                variant="outlined"
              />
              
              <Chip
                icon={<PublicIcon fontSize="small" />}
                label={member?.organization.country}
                variant="outlined"
              />
              
              <Chip
                icon={<WorkOutlineIcon fontSize="small" />}
                label={member?.organization.segment}
                variant="outlined"
              />
              
              <Chip
                icon={<CalendarMonthIcon fontSize="small" />}
                label={`Joined ${formatDate(member?.dateJoined || '')}`}
                variant="outlined"
              />
              
              <Chip
                icon={<AccessTimeIcon fontSize="small" />}
                label={`Active ${formatTimeSince(member?.lastActive || '')}`}
                variant="outlined"
              />
            </Box>

            {/* Connection/Message buttons based on status */}
            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
              {currentUser && currentUser.id !== id && (
                <>
                  {connectionStatus?.status === 'none' && (
                    <>
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<PersonAddIcon />}
                        onClick={handleConnectClick}
                        disabled={loadingConnection}
                      >
                        {loadingConnection ? 'Connecting...' : 'Connect'}
                      </Button>
                      <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<MessageIcon />}
                        onClick={handleMessageClick}
                      >
                        Send Message
                      </Button>
                    </>
                  )}

                  {connectionStatus?.status === 'pending' && (
                    <>
                      <Button
                        variant="outlined"
                        color="secondary"
                        startIcon={<PendingIcon />}
                        disabled
                      >
                        {connectionStatus?.connection?.isRequester ? 'Request Sent' : 'Request Received'}
                      </Button>
                      <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<MessageIcon />}
                        onClick={handleMessageClick}
                      >
                        Send Message
                      </Button>
                    </>
                  )}

                  {connectionStatus?.status === 'accepted' && (
                    <>
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<MessageIcon />}
                        onClick={handleMessageClick}
                      >
                        Message
                      </Button>
                      <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<EmailIcon />}
                        onClick={handleContactClick}
                      >
                        Email
                      </Button>
                    </>
                  )}

                  {connectionStatus?.status === 'rejected' && (
                    <>
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<PersonAddIcon />}
                        onClick={handleConnectClick}
                        disabled={loadingConnection}
                      >
                        {loadingConnection ? 'Connecting...' : 'Connect'}
                      </Button>
                      <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<MessageIcon />}
                        onClick={handleMessageClick}
                      >
                        Send Message
                      </Button>
                    </>
                  )}
                </>
              )}

              {!currentUser && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => navigate('/login')}
                >
                  Login to Connect
                </Button>
              )}

              {currentUser && currentUser.id === id && (
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => navigate('/profile')}
                >
                  Edit Profile
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Main Content */}
      <Grid container spacing={4}>
        {/* Left Column */}
        <Grid item xs={12} md={8}>
          {/* Expertise and Experience */}
          <Paper
            elevation={2}
            sx={{
              p: 3,
              borderRadius: 3,
              mb: 4
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
              Areas of Expertise
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 4 }}>
              {member?.selectedSectors.map((sector, index) => (
                <Chip
                  key={index}
                  icon={<ScienceIcon fontSize="small" />}
                  label={sector}
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.2),
                    }
                  }}
                />
              ))}
            </Box>

            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, mt: 4 }}>
              Experience
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
              {member?.experience}
            </Typography>
          </Paper>
        </Grid>

        {/* Right Column */}
        <Grid item xs={12} md={4}>
          {/* Organization Info */}
          <Card
            elevation={2}
            sx={{
              borderRadius: 3,
              mb: 4,
              height: '100%'
            }}
          >
            <CardContent>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
                Organization
              </Typography>
              <Divider sx={{ mb: 3 }} />

              <Typography variant="h6" gutterBottom sx={{ color: theme.palette.primary.main }}>
                {member?.organization.name}
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <WorkOutlineIcon fontSize="small" color="primary" sx={{ mr: 1 }} />
                <Typography variant="body2">
                  {member?.organization.segment}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <PublicIcon fontSize="small" color="primary" sx={{ mr: 1 }} />
                <Typography variant="body2">
                  {member?.organization.country}
                </Typography>
              </Box>

              <Typography variant="body2" paragraph sx={{ whiteSpace: 'pre-line' }}>
                {member?.organization.brief}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Related Members Section - could be added later */}
      {/* <Box sx={{ mt: 6 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
          Similar Members
        </Typography>
        <Grid container spacing={3}>
          {/* Map through similar members here */}
      {/* </Grid>
      </Box> */}
    </Container>
  );
};

export default MemberDetailPage;